import Skeleton from '@mui/material/Skeleton';
import { clone, round } from 'lodash-es';
import moment from 'moment';
import { type JSX, useState } from 'react';

import { DashboardApi } from 'Api/Dashboards/DashboardApi';
import { GroupedBarsChart } from 'Components/BaseCharts/GroupedBarsChart';
import { CSSColors } from 'Components/Colors';
import { FormFieldDateOptionSelect } from 'Components/FormField/FormFieldDateOptionSelect/FormFieldDateOptionSelect';
import { FormFieldSelectDark } from 'Components/FormField/FormFieldSelectDark/FormFieldSelectDark';
import { CustomDateRangeModal } from 'Components/Modal/CustomDateRangeModal/CustomDateRangeModal';
import { Text } from 'Components/Text/Text';
import { getFrameworkGroupControlParts } from 'Helpers/ControlFormatter/ControlFormatter';
import { useDateFilter } from 'Hooks/DateFilter';
import { ControlStrengthResponse, ControlsSnapshot, DateOptionSelectOption, DefaultPastDateRangeOptions, MonthlyEnabledControlsSnapshot } from 'Models/Dashboards';
import { CompletedAssessment, Effectiveness, effectivenessAsString } from 'Models/OperationalControls';
import { OptionType } from 'Models/Types/GlobalType';

import { buildControlEffectivenessSnapshotByMonth, buildControlOverTimeChartByMonth, stringToEffectiveness } from '../OperationalControlsDashboard.helpers';
import { useControlsHistoryQuery, useMonthlyEnabledControlSnapshotQuery } from '../OperationalControlsDashboard.hooks';
import styles from './ControlsOverTime.module.css';
import { ControlsOverTimeDisplayModal } from './ControlsOverTimeDisplayModal/ControlsOverTimeDisplayModal';

enum Modals {
    NONE,
    DISPLAY_CONTROLS,
    CUSTOM_DATE,
}

enum ControlChartType {
    COMPLETED_ASSESSMENTS = 'Completed Assessments',
    OVERALL_CONTROL_EFFECTIVENESS = 'Overall Control Effectiveness',
}

const DashboardRiskOptions: OptionType[] = [
    {
        value: 'Completed Assessments',
        label: ControlChartType.COMPLETED_ASSESSMENTS,
    },
    {
        value: 'Overall Control Effectiveness',
        label: ControlChartType.OVERALL_CONTROL_EFFECTIVENESS,
    },
];

export interface ControlsOverTimeProps {
    controlStrength?: ControlStrengthResponse;
    controlStrengthError?: Error;
    dashboardApi: DashboardApi;
}

export const ControlsOverTime = (props: ControlsOverTimeProps): JSX.Element => {
    const [modalState, setModalState] = useState<Modals>(Modals.NONE);
    const [effectivenessClicked, setEffectivenessClicked] = useState<Effectiveness>();
    const [controlsClicked, setControlsClicked] = useState<CompletedAssessment[]>();
    const [dateClicked, setDateClicked] = useState<string>();
    const dateFilterHookValues = useDateFilter(DateOptionSelectOption.LAST_THREE_MONTHS);
    const { controlsHistory, controlHistoryError } = useControlsHistoryQuery(props.dashboardApi, dateFilterHookValues.selection.dateRange);
    const { monthlyEnabledControlSnapshots, monthlyEnabledControlSnapshotsError, maximumSnapshotControlCount } = useMonthlyEnabledControlSnapshotQuery(props.dashboardApi, dateFilterHookValues.selection.dateRange);
    const lineColor = CSSColors.LIGHT_BLUE;
    const [chartType, setChartType] = useState<ControlChartType>(ControlChartType.COMPLETED_ASSESSMENTS);

    const completedAssessmentsChartData = () => {
        return buildControlOverTimeChartByMonth(controlsHistory!, [Effectiveness.FAIL, Effectiveness.WEAK, Effectiveness.MODERATE, Effectiveness.STRONG, Effectiveness.ROBUST]);
    };

    const completedAssessmentsControlsMaximum = () => {
        const assessments = controlsHistory!.map((controls) => controls.assessments);
        const largestAmountOfControls = Math.max(...assessments.map((am) => am.length));
        return Math.ceil(largestAmountOfControls / 5) * 5;
    };

    const handleChartClick = (monthYearEffectiveness: string) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [month, year, _pipe, effectiveness] = monthYearEffectiveness.split(' ');
        if (stringToEffectiveness(effectiveness) !== undefined) {
            const clickedEffectiveness = stringToEffectiveness(effectiveness);
            const formattedDateClicked = `${month} ${year}`;
            const actualMonth = moment(formattedDateClicked, 'MMM YY');
            const monthAsNumber = actualMonth.month() + 1; // Moment counts months 0-11; SummIT counts 1-12.
            const yearAsNumber = actualMonth.year();
            if (chartType === ControlChartType.OVERALL_CONTROL_EFFECTIVENESS) {
                const selectedAssessmentControls = monthlyEnabledControlSnapshots!
                    .filter((snapshot) => {
                        return snapshot.month.month === monthAsNumber && snapshot.month.year === yearAsNumber;
                    })
                    .flatMap((selectedSnapshots) => {
                        return selectedSnapshots.controls.filter((control) => {
                            return control.effectiveness.control_effectiveness === clickedEffectiveness;
                        });
                    })
                    .map((controlSnapshot) => {
                        return {
                            control_framework: getFrameworkGroupControlParts(controlSnapshot).controlFramework,
                            control_group_id: getFrameworkGroupControlParts(controlSnapshot).controlGroupId,
                            control_id: getFrameworkGroupControlParts(controlSnapshot).controlId,
                            control_name: controlSnapshot.metadata.control_name,
                            control_identifier: controlSnapshot.identifier,
                            effectiveness: controlSnapshot.effectiveness.control_effectiveness,
                            timestamp: controlSnapshot.effectiveness.last_updated,
                        } as CompletedAssessment;
                    });
                setControlsClicked(selectedAssessmentControls);
            } else {
                const selectedControls = controlsHistory!
                    .filter((controls) => {
                        return controls.month.month === monthAsNumber && controls.month.year === yearAsNumber;
                    })
                    .flatMap((selected) => {
                        return selected.assessments.filter((assessment) => {
                            return assessment.effectiveness === clickedEffectiveness;
                        });
                    });
                setControlsClicked(selectedControls);
            }
            setEffectivenessClicked(clickedEffectiveness);
            setModalState(Modals.DISPLAY_CONTROLS);
            setDateClicked(formattedDateClicked);
        }
    };

    const averageControlMap = (snapshot: ControlsSnapshot[]) => {
        const today = moment();
        const snapShotCopy = clone(snapshot);
        if (snapShotCopy[snapShotCopy.length - 1].month.month === today.month() + 1 && snapshot[snapShotCopy.length - 1]?.month.year === today.year()) {
            const currentSnapshot = snapShotCopy.pop();
            snapShotCopy.push({
                assessments: currentSnapshot!.assessments,
                average_effectiveness: props.controlStrength!.controls_effectiveness_average,
                month: currentSnapshot!.month,
            });
            return snapShotCopy.map((control) => round(control.average_effectiveness, 1));
        }
        return snapshot.map((control) => round(control.average_effectiveness, 1));
    };

    const monthlyAverageControlEffectiveness = (snapshot: MonthlyEnabledControlsSnapshot[]) => {
        return snapshot.map((monthlySnapshot) => round(monthlySnapshot.average_control_effectiveness, 1));
    };

    const monthlyControlSnapshotsChartData = (() => {
        if (monthlyEnabledControlSnapshots) {
            return buildControlEffectivenessSnapshotByMonth(monthlyEnabledControlSnapshots, [Effectiveness.UNRATED, Effectiveness.FAIL, Effectiveness.WEAK, Effectiveness.MODERATE, Effectiveness.STRONG, Effectiveness.ROBUST]);
        }
    })();

    if (props.controlStrengthError) {
        return (
            <>
                <div className={styles.cellHeader}>
                    <Text variant="Header2" color="white">
                        Control Effectiveness Over Time
                    </Text>
                </div>
                <Text color="white">{props.controlStrengthError.message}</Text>
            </>
        );
    }

    if (controlHistoryError || monthlyEnabledControlSnapshotsError) {
        return (
            <>
                <div className={styles.cellHeader}>
                    <Text variant="Header2" color="white">
                        Control Effectiveness Over Time
                    </Text>
                </div>
                <Text color="white" variant="Header4">
                    {controlHistoryError || monthlyEnabledControlSnapshotsError}
                </Text>
            </>
        );
    }

    return (
        <>
            {modalState === Modals.CUSTOM_DATE && <CustomDateRangeModal datesRestriction="pastDatesOnly" hideModal={() => setModalState(Modals.NONE)} dateFilterHookValues={dateFilterHookValues} />}
            {modalState === Modals.DISPLAY_CONTROLS && controlsClicked && dateClicked && effectivenessClicked !== undefined && <ControlsOverTimeDisplayModal hideModal={() => setModalState(Modals.NONE)} selectedControls={controlsClicked} effectiveness={effectivenessClicked} date={dateClicked} />}
            <div className={styles.cellHeader}>
                <Text variant="Header2" color="white">
                    Control Effectiveness Over Time
                </Text>
                <div className={styles.cellHeader}>
                    <FormFieldSelectDark formFieldId="graph choice" formFieldLabel="Graph Choice" handleChange={(value) => value && setChartType(value as ControlChartType)} options={DashboardRiskOptions} selectedOption={chartType} />
                    <FormFieldDateOptionSelect formFieldId="controlsOverTime" options={DefaultPastDateRangeOptions} disabled={controlsHistory === undefined || props.controlStrength === undefined} onSelectCustom={() => setModalState(Modals.CUSTOM_DATE)} dateFilterHookValues={dateFilterHookValues} />
                </div>
            </div>
            {controlsHistory && props.controlStrength && monthlyControlSnapshotsChartData && monthlyEnabledControlSnapshots && maximumSnapshotControlCount ? (
                chartType === ControlChartType.OVERALL_CONTROL_EFFECTIVENESS ? (
                    <GroupedBarsChart
                        key={ControlChartType.OVERALL_CONTROL_EFFECTIVENESS}
                        onChartClick={handleChartClick}
                        xAxisLabels={monthlyEnabledControlSnapshots.map(
                            (assessment) =>
                                `${moment()
                                    .month(assessment.month.month - 1)
                                    .format('MMM')} ${moment().year(assessment.month.year).format('YY')}`
                        )}
                        barsSeriesArray={monthlyControlSnapshotsChartData}
                        groupingMode="stacked"
                        yAxisInfo={[
                            { name: 'Number of Controls', position: 'left', min: 0, max: Math.ceil(maximumSnapshotControlCount / 5) * 5 },
                            {
                                name: 'Average Control Effectiveness',
                                position: 'right',
                                max: 5,
                                min: 0,
                                interval: 1,
                                labelFormatter: (item) => effectivenessAsString(item),
                            },
                        ]}
                        line={{ name: 'Average Control Effectiveness', color: lineColor, data: monthlyAverageControlEffectiveness(monthlyEnabledControlSnapshots) }}
                    />
                ) : (
                    <GroupedBarsChart
                        key={ControlChartType.COMPLETED_ASSESSMENTS}
                        onChartClick={handleChartClick}
                        xAxisLabels={controlsHistory.map(
                            (control) =>
                                `${moment()
                                    .month(control.month.month - 1)
                                    .format('MMM')} ${moment().year(control.month.year).format('YY')}`
                        )}
                        barsSeriesArray={completedAssessmentsChartData()}
                        groupingMode="stacked"
                        yAxisInfo={[
                            { name: 'Number of Controls', position: 'left', max: completedAssessmentsControlsMaximum(), min: 0, interval: completedAssessmentsControlsMaximum() / 5 },
                            {
                                name: 'Average Control Effectiveness',
                                position: 'right',
                                max: 5,
                                min: 0,
                                interval: 1,
                                labelFormatter: (item) => effectivenessAsString(item),
                            },
                        ]}
                        line={{ name: 'Average Control Effectiveness', color: lineColor, data: averageControlMap(controlsHistory) }}
                    />
                )
            ) : (
                <Skeleton variant="rounded" width={'100%'} height={300} />
            )}
        </>
    );
};
